import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input } from '../../design-system-integration';
import { useAuth } from '../../context/AuthContext';
import { RegisterData } from '../../context/AuthContext';

/**
 * Register Component (Fixed Version)
 *
 * This version of the Register component uses CSS variables instead of
 * styled-components' theme prop to avoid the "Cannot read properties of undefined" error.
 *
 * It follows Module M7 (Integration Points) principles by providing a clear
 * integration point between the component and the theme system.
 */

// Use CSS variables instead of theme props
const RegisterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const Title = styled.h1`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const LoginPrompt = styled.div`
  margin-top: var(--spacing-lg);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
`;

const LoginLink = styled(Link)`
  color: var(--primary-500);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-default);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
  background-color: var(--error-light);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const FormRow = styled.div`
  display: flex;
  gap: var(--spacing-md);
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

// Create a styled wrapper for the submit button
const SubmitWrapper = styled.div`
  width: 100%;
  display: block;
`;

const Register: React.FC = () => {
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    church: '',
    role: ''
  });
  
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (formData.password !== formData.password_confirm) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setError(null);
      setIsLoading(true);
      
      await register(formData);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to register. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div>
      <Title>Create Account</Title>
      <Subtitle>Join ConnectPath to start your journey</Subtitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <RegisterForm onSubmit={handleSubmit}>
        <FormRow>
          <Input
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            placeholder="Enter your first name"
            fullWidth
            required
            autoComplete="given-name"
          />
          
          <Input
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Enter your last name"
            fullWidth
            required
            autoComplete="family-name"
          />
        </FormRow>
        
        <Input
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          fullWidth
          required
          autoComplete="email"
        />
        
        <Input
          label="Church Name (Optional)"
          name="church"
          value={formData.church}
          onChange={handleChange}
          placeholder="Enter your church name"
          fullWidth
          autoComplete="organization"
        />
        
        <Input
          label="Role (Optional)"
          name="role"
          value={formData.role}
          onChange={handleChange}
          placeholder="Enter your role (e.g., Pastor, Admin)"
          fullWidth
          autoComplete="organization-title"
        />
        
        <Input
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Create a password"
          fullWidth
          required
          autoComplete="new-password"
        />
        
        <Input
          label="Confirm Password"
          type="password"
          name="password_confirm"
          value={formData.password_confirm}
          onChange={handleChange}
          placeholder="Confirm your password"
          fullWidth
          required
          autoComplete="new-password"
        />
        
        <FormActions>
          <SubmitWrapper>
            <Button
              variant="primary"
              fullWidth
              disabled={isLoading}
              onClick={isLoading ? undefined : (e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </SubmitWrapper>
        </FormActions>
      </RegisterForm>
      
      <LoginPrompt>
        Already have an account? <LoginLink to="/login">Sign in</LoginLink>
      </LoginPrompt>
    </div>
  );
};

export default Register;
